/**
 * 图层编辑器 store（方案 4.8）
 * 编辑期状态 + 文档数据 + 历史栈；快照 = 深拷贝 LayerDocument（不含缩略图，缩略图在 node.data 侧）。
 */
import { create } from 'zustand';
import type { LayerDocument, LayerItem, LayerTransform, LayerBackground } from '@/types/layerEdit';
import { createLayerDocument } from '@/types/layerEdit';

const HISTORY_LIMIT = 50;

let layerSeq = 0;
export function nextLayerId(): string {
  layerSeq += 1;
  return `layer-${Date.now()}-${layerSeq}`;
}

function cloneDoc(doc: LayerDocument | null): LayerDocument | null {
  if (!doc) return null;
  return typeof structuredClone === 'function'
    ? structuredClone(doc)
    : (JSON.parse(JSON.stringify(doc)) as LayerDocument);
}

interface LayerEditState {
  isOpen: boolean;
  activeNodeId: string | null;
  projectId: string | null;
  doc: LayerDocument | null;
  selectedLayerIds: string[];
  dirty: boolean;
  activeLeftPanel: 'canvasImages' | 'history' | null;
  activeToolPopover: 'opacity' | 'align' | null;
  /** 浮层锚点：触发按钮中心相对浮层挂载容器的 y（浮层贴按钮旁弹出，null 回退垂直居中） */
  toolPopoverAnchorY: number | null;
  isAdjustPanelOpen: boolean;
  isGridComposeOpen: boolean;
  isCutoutSettingsOpen: boolean;
  isTextRetouchOpen: boolean;
  isBoxCutoutMode: boolean;
  isCropMode: boolean;
  /** AI 类工具（文本修图等）进行中标志——关闭确认与并发保护（4.2/4.6.3） */
  isAITaskRunning: boolean;
  aiTargetLayerId: string | null;
  setAITaskRunning: (v: boolean, targetLayerId?: string | null) => void;
  viewScale: number;
  /** 视口平移（Stage x/y，与 viewScale 共同构成视口；fit 与滚轮缩放统一经 store 驱动） */
  viewOffset: { x: number; y: number };
  setViewOffset: (o: { x: number; y: number }) => void;
  past: LayerDocument[];
  future: LayerDocument[];

  openEditor: (nodeId: string, projectId: string | null, initialDoc?: LayerDocument | null) => void;
  closeEditor: () => void;
  markSaved: () => void;
  setDirty: (v: boolean) => void;
  setDoc: (doc: LayerDocument) => void;

  setRatio: (ratio: string, w: number, h: number) => void;
  setBackground: (bg: LayerBackground) => void;

  addLayer: (item: LayerItem, opts?: { silent?: boolean }) => void;
  removeLayers: (ids: string[]) => void;
  /** 本会话已删除、待保存确认后断开来源连线的图层队列——「直接关闭」丢弃队列即删除不生效、连线保持 */
  pendingRemovedLayers: LayerItem[];
  /** 保存成功后消费队列（返回并清空），由调用方执行断线 */
  consumePendingRemovedLayers: () => LayerItem[];
  duplicateLayer: (id: string) => void;
  reorderLayer: (id: string, toIndex: number) => void;
  moveLayerToTop: (id: string) => void;
  moveLayerToBottom: (id: string) => void;
  updateTransform: (id: string, patch: Partial<LayerTransform>) => void;
  updateLayer: (id: string, patch: Partial<LayerItem>) => void;
  replaceLayerSource: (id: string, patch: Partial<LayerItem>) => void;

  select: (id: string | null) => void;
  toggleSelect: (id: string) => void;
  rangeSelect: (id: string) => void;
  clearSelection: () => void;
  setSelection: (ids: string[]) => void;

  pushHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  setLeftPanel: (p: 'canvasImages' | 'history' | null) => void;
  setToolPopover: (p: 'opacity' | 'align' | null, anchorY?: number | null) => void;
  setAdjustPanel: (open: boolean, anchorY?: number | null) => void;
  setGridCompose: (open: boolean) => void;
  setCutoutSettings: (open: boolean) => void;
  setTextRetouch: (open: boolean, anchorY?: number | null) => void;
  setBoxCutoutMode: (open: boolean) => void;
  setCropMode: (open: boolean) => void;
  setViewScale: (s: number) => void;
}

export const useLayerEditStore = create<LayerEditState>((set, get) => ({
  isOpen: false,
  activeNodeId: null,
  projectId: null,
  doc: null,
  pendingRemovedLayers: [],
  selectedLayerIds: [],
  dirty: false,
  activeLeftPanel: null,
  activeToolPopover: null,
  toolPopoverAnchorY: null,
  isAdjustPanelOpen: false,
  isGridComposeOpen: false,
  isCutoutSettingsOpen: false,
  isTextRetouchOpen: false,
  isBoxCutoutMode: false,
  isCropMode: false,
  isAITaskRunning: false,
  aiTargetLayerId: null,
  setAITaskRunning: (v, targetLayerId) =>
    set({ isAITaskRunning: v, aiTargetLayerId: v ? (targetLayerId ?? null) : null }),
  viewScale: 1,
  viewOffset: { x: 60, y: 60 },
  setViewOffset: (o) => set({ viewOffset: o }),
  past: [],
  future: [],

  openEditor: (nodeId, projectId, initialDoc) =>
    set({
      isOpen: true,
      activeNodeId: nodeId,
      projectId,
      doc: initialDoc ? cloneDoc(initialDoc) : createLayerDocument('16:9', 1920, 1080),
      selectedLayerIds: [],
      dirty: false,
      pendingRemovedLayers: [],
      activeLeftPanel: null,
      activeToolPopover: null,
      isAdjustPanelOpen: false,
      isGridComposeOpen: false,
      isCutoutSettingsOpen: false,
      isTextRetouchOpen: false,
      isBoxCutoutMode: false,
      isCropMode: false,
      viewScale: 1,
      past: [],
      future: [],
    }),

  closeEditor: () =>
    set({
      isOpen: false,
      activeNodeId: null,
      projectId: null,
      doc: null,
      selectedLayerIds: [],
      dirty: false,
      pendingRemovedLayers: [],
      activeLeftPanel: null,
      activeToolPopover: null,
      isAdjustPanelOpen: false,
      isGridComposeOpen: false,
      isCutoutSettingsOpen: false,
      isTextRetouchOpen: false,
      isBoxCutoutMode: false,
      isCropMode: false,
      past: [],
      future: [],
    }),

  markSaved: () => set({ dirty: false }),
  setDirty: (v) => set({ dirty: v }),
  setDoc: (doc) => set({ doc, dirty: true }),

  setRatio: (ratio, w, h) => {
    get().pushHistory();
    set((s) => ({
      doc: s.doc ? { ...s.doc, ratio, canvasWidth: w, canvasHeight: h } : s.doc,
      dirty: true,
    }));
  },

  setBackground: (bg) => {
    get().pushHistory();
    set((s) => ({ doc: s.doc ? { ...s.doc, background: bg } : s.doc, dirty: true }));
  },

  addLayer: (item, opts) => {
    if (!opts?.silent) get().pushHistory();
    set((s) => ({
      doc: s.doc ? { ...s.doc, layers: [...s.doc.layers, item] } : s.doc,
      selectedLayerIds: [item.id],
      dirty: true,
    }));
  },

  removeLayers: (ids) => {
    const removedLayers = get().doc?.layers.filter((l) => ids.includes(l.id)) ?? [];
    get().pushHistory();
    // 4.10 修订（v0.42）：删除只记账不即时断线——断开来源连线延后到保存确认
    //（manualSave/autoSave 经 consumePendingRemovedLayers 消费队列后执行），
    // 「直接关闭」丢弃队列即删除不生效、画布连线保持不断
    set((s) => ({
      doc: s.doc ? { ...s.doc, layers: s.doc.layers.filter((l) => !ids.includes(l.id)) } : s.doc,
      selectedLayerIds: s.selectedLayerIds.filter((id) => !ids.includes(id)),
      dirty: true,
      pendingRemovedLayers: [
        ...s.pendingRemovedLayers,
        ...removedLayers.filter((l) => !s.pendingRemovedLayers.some((p) => p.id === l.id)),
      ],
    }));
  },

  consumePendingRemovedLayers: () => {
    const pending = get().pendingRemovedLayers;
    set({ pendingRemovedLayers: [] });
    return pending;
  },

  duplicateLayer: (id) => {
    get().pushHistory();
    set((s) => {
      if (!s.doc) return s;
      const idx = s.doc.layers.findIndex((l) => l.id === id);
      if (idx < 0) return s;
      const src = s.doc.layers[idx];
      const copy: LayerItem = {
        ...structuredCloneSafe(src),
        id: nextLayerId(),
        name: src.name,
        transform: { ...src.transform, x: src.transform.x + 16, y: src.transform.y + 16 },
      };
      const layers = [...s.doc.layers];
      layers.splice(idx + 1, 0, copy);
      return { doc: { ...s.doc, layers }, selectedLayerIds: [copy.id], dirty: true };
    });
  },

  reorderLayer: (id, toIndex) => {
    get().pushHistory();
    set((s) => {
      if (!s.doc) return s;
      const layers = [...s.doc.layers];
      const from = layers.findIndex((l) => l.id === id);
      if (from < 0) return s;
      const [item] = layers.splice(from, 1);
      const clamped = Math.max(0, Math.min(toIndex, layers.length));
      layers.splice(clamped, 0, item);
      return { doc: { ...s.doc, layers }, dirty: true };
    });
  },

  moveLayerToTop: (id) => {
    get().pushHistory();
    set((s) => {
      if (!s.doc) return s;
      const layers = s.doc.layers.filter((l) => l.id !== id);
      const item = s.doc.layers.find((l) => l.id === id);
      if (!item) return s;
      return { doc: { ...s.doc, layers: [...layers, item] }, dirty: true };
    });
  },

  moveLayerToBottom: (id) => {
    get().pushHistory();
    set((s) => {
      if (!s.doc) return s;
      const layers = s.doc.layers.filter((l) => l.id !== id);
      const item = s.doc.layers.find((l) => l.id === id);
      if (!item) return s;
      return { doc: { ...s.doc, layers: [item, ...layers] }, dirty: true };
    });
  },

  /** 手势中连续更新：只改数据不入历史（commitTransform 在手势结束时入栈） */
  updateTransform: (id, patch) =>
    set((s) => {
      if (!s.doc) return s;
      return {
        doc: {
          ...s.doc,
          layers: s.doc.layers.map((l) => (l.id === id ? { ...l, transform: { ...l.transform, ...patch } } : l)),
        },
        dirty: true,
      };
    }),

  /** 通用图层字段更新（adjust/blendMode/shadow/locked/visible 等，调用方决定是否入栈） */
  updateLayer: (id, patch) =>
    set((s) => {
      if (!s.doc) return s;
      return {
        doc: { ...s.doc, layers: s.doc.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) },
        dirty: true,
      };
    }),

  /** 换源（抠图/裁切产物），含快照入栈（离散操作） */
  replaceLayerSource: (id, patch) => {
    get().pushHistory();
    set((s) => {
      if (!s.doc) return s;
      return {
        doc: { ...s.doc, layers: s.doc.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) },
        dirty: true,
      };
    });
  },

  select: (id) => set({ selectedLayerIds: id ? [id] : [], activeToolPopover: null }),

  toggleSelect: (id) =>
    set((s) => ({
      selectedLayerIds: s.selectedLayerIds.includes(id)
        ? s.selectedLayerIds.filter((x) => x !== id)
        : [...s.selectedLayerIds, id],
    })),

  rangeSelect: (id) =>
    set((s) => {
      if (!s.doc) return { selectedLayerIds: [id] };
      const layers = s.doc.layers;
      const anchorId = s.selectedLayerIds[s.selectedLayerIds.length - 1] ?? id;
      const anchor = layers.findIndex((l) => l.id === anchorId);
      const target = layers.findIndex((l) => l.id === id);
      if (anchor < 0 || target < 0) return { selectedLayerIds: [id] };
      const [from, to] = anchor <= target ? [anchor, target] : [target, anchor];
      const ids = layers.slice(from, to + 1).map((l) => l.id);
      return { selectedLayerIds: Array.from(new Set([...s.selectedLayerIds, ...ids])) };
    }),

  clearSelection: () => set({ selectedLayerIds: [], activeToolPopover: null, isAdjustPanelOpen: false }),

  /** 框选结果整体写入选区（marquee 拖拽中连续调用，内容未变时跳过） */
  setSelection: (ids) =>
    set((s) => {
      if (s.selectedLayerIds.length === ids.length && s.selectedLayerIds.every((id) => ids.includes(id))) return s;
      return { selectedLayerIds: ids, activeToolPopover: null };
    }),

  pushHistory: () => {
    const { doc, past } = get();
    if (!doc) return;
    const snapshot = cloneDoc(doc)!;
    // 与栈顶相同则不入栈（避免无效快照）
    if (past.length > 0 && JSON.stringify(past[past.length - 1]) === JSON.stringify(snapshot)) return;
    set({ past: [...past, snapshot].slice(-HISTORY_LIMIT), future: [] });
  },

  undo: () => {
    const { past, doc, future, pendingRemovedLayers } = get();
    if (past.length === 0 || !doc) return;
    const prev = past[past.length - 1];
    const nextDoc = cloneDoc(prev)!;
    // 撤销删除：被恢复的图层移出待断线队列（连线不应断）
    const restoredIds = new Set(
      nextDoc.layers.filter((l) => !doc.layers.some((c) => c.id === l.id)).map((l) => l.id)
    );
    set({
      doc: nextDoc,
      past: past.slice(0, -1),
      future: [cloneDoc(doc)!, ...future].slice(0, HISTORY_LIMIT),
      dirty: true,
      pendingRemovedLayers: pendingRemovedLayers.filter((l) => !restoredIds.has(l.id)),
    });
  },

  redo: () => {
    const { past, doc, future, pendingRemovedLayers } = get();
    if (future.length === 0 || !doc) return;
    const next = future[0];
    const nextDoc = cloneDoc(next)!;
    // 重做删除：被重新删除的图层记回待断线队列（保存时统一断线）
    const removedAgain = doc.layers.filter((l) => !nextDoc.layers.some((c) => c.id === l.id));
    const pendingIds = new Set(pendingRemovedLayers.map((l) => l.id));
    set({
      doc: nextDoc,
      past: [...past, cloneDoc(doc)!].slice(-HISTORY_LIMIT),
      future: future.slice(1),
      dirty: true,
      pendingRemovedLayers: [...pendingRemovedLayers, ...removedAgain.filter((l) => !pendingIds.has(l.id))],
    });
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  setLeftPanel: (p) => set({ activeLeftPanel: p }),
  // 右侧工具栏浮层互斥（单选，v0.37 含文本修图共四方）：打开其一必收起其余浮层，锚点随触发按钮更新
  setToolPopover: (p, anchorY = null) =>
    set(p
      ? { activeToolPopover: p, toolPopoverAnchorY: anchorY, isAdjustPanelOpen: false, isTextRetouchOpen: false }
      : { activeToolPopover: null, toolPopoverAnchorY: null }),
  setAdjustPanel: (open, anchorY = null) =>
    set(open
      ? { isAdjustPanelOpen: true, toolPopoverAnchorY: anchorY, activeToolPopover: null, isTextRetouchOpen: false }
      : { isAdjustPanelOpen: false, toolPopoverAnchorY: null }),
  setGridCompose: (open) => {
    if (open) closeFloatingUI();
    set({ isGridComposeOpen: open });
  },
  setCutoutSettings: (open) => set({ isCutoutSettingsOpen: open }),
  setTextRetouch: (open, anchorY = null) => {
    if (open) closeFloatingUI();
    set(open
      ? { isTextRetouchOpen: true, toolPopoverAnchorY: anchorY }
      : { isTextRetouchOpen: false, toolPopoverAnchorY: null });
  },
  setBoxCutoutMode: (open) => {
    if (open) closeFloatingUI();
    set({ isBoxCutoutMode: open });
  },
  setCropMode: (open) => {
    if (open) closeFloatingUI();
    set({ isCropMode: open });
  },
  setViewScale: (s) => set({ viewScale: Math.max(0.1, Math.min(8, s)) }),
}));

/** 进入全屏模式时收起所有浮层/弹窗（方案 4.8 setEditorMode 分层管理） */
function closeFloatingUI() {
  useLayerEditStore.setState({
    activeLeftPanel: null,
    activeToolPopover: null,
    toolPopoverAnchorY: null,
    isAdjustPanelOpen: false,
    isGridComposeOpen: false,
    isTextRetouchOpen: false,
    isCutoutSettingsOpen: false,
  });
}

function structuredCloneSafe<T>(v: T): T {
  return typeof structuredClone === 'function' ? structuredClone(v) : (JSON.parse(JSON.stringify(v)) as T);
}
